/**
 * MODULE: AssistantWelcomeChipButton.tsx
 * PURPOSE: Context-aware welcome chip with self-learning usage badges for email templates.
 */

import type { AssistantWelcomeChip } from './assistantWelcomePrompts';
import { stripEmailTemplateUsesFromLabel } from './assistantWelcomePrompts';
import { EmailTemplateActionBadges } from './EmailTemplateActionBadges';

type AssistantWelcomeChipButtonProps = {
  chip: AssistantWelcomeChip;
  onClick: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  testId?: string;
};

export function AssistantWelcomeChipButton({
  chip,
  onClick,
  disabled = false,
  size = 'md',
  className = '',
  testId,
}: AssistantWelcomeChipButtonProps) {
  const displayLabel = stripEmailTemplateUsesFromLabel(chip.label);
  const padding = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={chip.prompt !== chip.label ? chip.prompt : undefined}
      data-testid={
        testId ??
        (chip.label.toLowerCase().includes('template')
          ? 'assistant-welcome-email-template-chip'
          : 'assistant-welcome-prompt-chip')
      }
      className={[
        'inline-flex max-w-full flex-col items-start gap-1 rounded-full border border-gray-200 bg-gray-100 text-left text-gray-700 transition-colors hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
        padding,
        'disabled:opacity-50',
        className,
      ].join(' ')}
    >
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="font-medium leading-snug">{displayLabel}</span>
        <EmailTemplateActionBadges recommended={chip.recommended} uses={chip.uses} />
      </span>
    </button>
  );
}
